// Story 7.11 (ADO-1142) — substitute-products client (FR105).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// SearchAndDiscovery substitute-lookup read. Mirrors `browse.client.ts` /
// `favourites.client.ts` / `availability.client.ts`: the SINGLE consumer seam
// until the `openapi-typescript`-generated client lands (7.1 D-6), exercised
// against msw in tests. The backend serves a bare `catalogue/products/:sku/
// substitutes` route (D-7); this client prefixes `API_PREFIX`.
//
// The wire shape is a LOCK-STEP COPY of the module's exported
// `SubstituteProductDto` (search-and-discovery contracts/dtos) — keep the two in
// sync. PRICE IS DELIBERATELY ABSENT (Deviation D-1): the platform cannot source
// a price yet (no pricing module, no projection column, Money not projected), so
// a substitute row carries name / image / pack only — the Story 7.9 posture.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { withEntry } from './catalogue-entry';

/** A substitute suggestion (lock-step with `SubstituteProductDto`). Availability
 *  (Story 7.3) is composed client-side, as in browse/search/favourites — and is
 *  what the panel uses to hide out-of-stock substitutes (OQ-5, D-10). NO price. */
export interface SubstituteProduct {
  readonly sku: string;
  readonly name: string;
  /** Primary product image URL, or `null` → the branded placeholder (Story 7.4). */
  readonly imageUrl: string | null;
  readonly packSize: string | null;
  /** Picked fresh: no stock state, always orderable (Enabler E7-B, ADO-2273; MP AVL-5.4). The
   *  availability read leaves it out, so the panel offers it without a stock entry. */
  readonly isPickedFresh: boolean;
}

/** The bare backend segment (after `API_PREFIX`) for a product's substitutes. */
export function substitutesPath(sku: string): string {
  return `${API_PREFIX}/catalogue/products/${encodeURIComponent(sku)}/substitutes`;
}

/**
 * Fetch the substitute suggestions for `sku` in the selected ordering `entry`'s range,
 * at its depot (AC1; Enabler E7-C, ADO-2274, D-1). The server OVER-FETCHES up to
 * `MAX_SUBSTITUTE_CANDIDATES` (10, get-substitutes.service.ts); the ≤3 DISPLAY cap moved
 * to the client and is applied AFTER OOS-exclusion (Story 7-FU-I, AC2 — see
 * `MAX_SUBSTITUTES_DISPLAYED` in ProductCard.tsx). An out-of-stock product with no
 * configured substitutes returns `[]` (AC3: the panel renders no section). Only a
 * transport/non-2xx failure throws, so the caller can render an error state; an empty
 * list is a valid, non-error result.
 */
export async function fetchSubstitutes(
  entry: string,
  sku: string,
): Promise<readonly SubstituteProduct[]> {
  const res = await apiFetch(withEntry(substitutesPath(sku), entry));
  if (!res.ok) throw new Error(`Failed to load substitutes (${res.status})`);
  return (await res.json()) as SubstituteProduct[];
}
